import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Form, Row } from "antd";
import { omit } from "lodash";
import { useAppDispatch, useAppSelector } from "../../../../index";
import { getIndividualInfoById } from "../../../../store/slices/accountsTs";
import { checkIsLoaded, checkIsLoading } from "../../../../lib/utils";
import versionsTableUtils from "../../../../lib/utils/versionsTableUtils";
import {
  toDisplayFormat,
  toHttpFormat,
  toHttpFormatStartOfDay,
} from "../../../../lib/utils/formatDate";
import ToggleableVersionsTable from "../../../ToggleableVersionsTable/ToggleableVersionsTable";
import ChangeVersionsTable from "../../../common/ChangeVersionsTable";
import { TableRecord } from "../../../../lib/interfaces";
import {
  deleteProperties,
  fetchPropertiesVersions,
  updateProperty,
} from "../../../../store/slices/properties";

export const AccountContactsDetailsPosition = () => {
  const history = useHistory();
  const [propertyId, setPropertyId] = useState<number>();
  const individualId = history.location.search.substring(1).split("=")[1];
  const { getIndividualByIdData, getIndividualByIdLoading } = useAppSelector(
    (state) => state.accountsTs
  );
  const { propertiesVersions, propertiesVersionsLoading } = useAppSelector(
    (state) => state.properties
  );

  const [tableData, setTableData] = useState<any>([]);
  const [childTableData, setChildTableData] = useState<any>([]);
  const [form] = Form.useForm();
  const isChangingForbidden = versionsTableUtils.getIsTableChanging(childTableData);
  const dispatch = useAppDispatch();

  const columnsWidths = {
    ...versionsTableUtils.columnsWidths,
    position: "65%",
    period: "20%",
    actions: "15%",
  };

  useEffect(() => {
    if (checkIsLoaded(getIndividualByIdLoading)) {
      if (getIndividualByIdData?.properties.length > 0) {
        const positionObject = getIndividualByIdData?.properties?.find(
          (item) => item?.typeId === "position"
        );
        setTableData([
          {
            key: positionObject?.id,
            id: positionObject?.id,
            fromDate: positionObject?.fromDate,
            toDate: positionObject?.toDate,
            position: positionObject?.stringValue,
          },
        ]);
      } else {
        setTableData([]);
      }
    }
  }, [getIndividualByIdData]);

  useEffect(() => {
    if (checkIsLoaded(propertiesVersionsLoading)) {
      setChildTableData(
        propertiesVersions
          .map((item: any, i) => {
            return {
              id: item.id,
              fromDate: item.fromDate,
              toDate: item.toDate,
              position: item.stringValue,
              ...versionsTableUtils.getCommonChildTableProps(item, i, propertiesVersions.length),
            };
          })
          .sort(versionsTableUtils.sortVersionsByDate)
      );
    }
  }, [propertiesVersions]);

  const [columns, setColumns] = useState([
    {
      title: "Название должности",
      dataIndex: "position",
      key: "position",
      width: columnsWidths.position,
      hidden: false,
    },
    {
      title: "Период действия",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (v, record) => {
        if (record.fromDate && record.toDate) {
          return `${toDisplayFormat(record.fromDate)} - ${toDisplayFormat(record.toDate)}`;
        }
        return "";
      },
    },
    {
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      hidden: false,
      render: () => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }} />
      ),
    },
  ]);

  const getChildColumns = () => [
    {
      title: "Название должности",
      dataIndex: "position",
      key: "position",
      width: columnsWidths.position,
      hidden: false,
      render: (v, record) => (
        <ChangeVersionsTable.Input
          name="position"
          record={record}
          setFieldsValue={form.setFieldsValue}
          required
        />
      ),
    },
    {
      title: "Период",
      dataIndex: "period",
      key: "period",
      width: columnsWidths.period,
      hidden: false,
      render: (text, record) => (
        <ChangeVersionsTable.Period
          record={record}
          setFieldsValue={form.setFieldsValue}
          fromDateRequired
          toDateRequired
          disabledToDate={(current) =>
            current.isBefore(record.fromDate, "day") || current.isAfter(record.toDate, "day")
          }
        />
      ),
    },
    {
      title: "",
      dataIndex: "actions",
      key: "actions",
      width: columnsWidths.actions,
      hidden: false,
      render: (value, record) => {
        //  const isHasAccess = hasAccess(MENU_ACCOUNT_ACCOUNTINFO_WRITE);
        return (
          <>
            <ChangeVersionsTable.AddButton
              // TODO пока захардкожены hasAccess=true
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.EditButton
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.CloseButton
              hasAccess
              isChangingForbidden={isChangingForbidden}
              record={record}
              setChildTableData={setChildTableData}
            />
            <ChangeVersionsTable.SubmitButton record={record} submit={form.submit} />
            <ChangeVersionsTable.CancelButton
              record={record}
              resetFields={form.resetFields}
              setChildTableData={setChildTableData}
            />
          </>
        );
      },
    },
    {
      dataIndex: "buttons",
      key: "buttons",
      width: columnsWidths.buttons,
      render: () => (
        <div style={{ display: "flex", justifyContent: "end", alignItems: "center" }} />
      ),
    },
  ];

  const [childColumns, setChildColumns] = useState(getChildColumns());

  useEffect(() => {
    setChildColumns(getChildColumns());
  }, [isChangingForbidden]);

  const onFinish = (values) => {
    const version = childTableData.find(
      (item) => item.isCreated || item.isEditing || item.isClosing
    );
    const changeableRecord: TableRecord = versionsTableUtils.getChangeableRecord(childTableData);
    if (version.isCreated || version.isEditing) {
      const isForceUpdate = Boolean(changeableRecord.isEditing);
      const fromDate = isForceUpdate
        ? changeableRecord.fromDate
        : toHttpFormatStartOfDay(values.fromDate);

      const positionObject =
        propertiesVersions &&
        propertiesVersions?.find((item) => item.propertyType?.alias === "position");

      if (positionObject) {
        dispatch(
          updateProperty({
            id: changeableRecord?.id,
            isVersionUpdated: isForceUpdate,
            fromDate: toHttpFormatStartOfDay(fromDate),
            values: {
              ...omit(positionObject, "propertyType"),
              stringValue: values.position,
            },
          })
        ).then(({ payload }) => {
          if (payload) {
            dispatch(getIndividualInfoById({ individualId }));
            if (propertyId) {
              dispatch(fetchPropertiesVersions({ id: propertyId }));
            }
          }
        });
      }
    }

    if (version.isClosing && changeableRecord?.id) {
      dispatch(
        deleteProperties({
          id: changeableRecord.id,
          fromDate: toHttpFormat(values.toDate),
        })
      ).then(() => {
        dispatch(getIndividualInfoById({ individualId }));
        if (propertyId) {
          dispatch(fetchPropertiesVersions({ id: propertyId }));
        }
      });
    }
  };

  return (
    <div className="account-details-table-wrapper">
      <Form form={form} onFinish={onFinish}>
        <ToggleableVersionsTable
          title={
            <Row align="middle">
              <h4 style={{ margin: 0 }}>Должность</h4>
            </Row>
          }
          tableName="account-details-table"
          columns={columns}
          setColumns={setColumns}
          tableData={tableData}
          childTableColumns={childColumns}
          setChildTableColumns={setChildColumns}
          childTableData={childTableData}
          isToggleableColumns={false}
          isChildTableLoading={checkIsLoading(propertiesVersionsLoading)}
          onOpenVersionsCallback={(record, expanded) => {
            if (!expanded && record?.id) {
              dispatch(fetchPropertiesVersions({ id: record.id }));
              setPropertyId(record.id);
            }
          }}
          isPageable={false}
        />
      </Form>
    </div>
  );
};
